import { useEffect, useState } from 'react';
import { Card, CardContent, Typography, Box, CircularProgress, alpha } from '@mui/material';
import Grid from '@mui/material/Grid';
import KeyIcon from '@mui/icons-material/KeyOutlined';
import DevicesIcon from '@mui/icons-material/DevicesOutlined';
import PeopleIcon from '@mui/icons-material/PeopleOutlined';
import WarningIcon from '@mui/icons-material/WarningOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUpOutlined';
import { colors } from '../../theme';

// Use relative URL in production (same origin), configurable for development
const API_URL = import.meta.env.VITE_API_URL || '';

interface DashboardStats {
    activeTokens: number;
    activeDevices: number;
    totalUsers: number;
    expiringSoon: number;
}

interface StatCardProps {
    title: string;
    value: number;
    icon: React.ReactNode;
    color: string;
    subtitle?: string;
}

const StatCard = ({ title, value, icon, color, subtitle }: StatCardProps) => (
    <Card
        sx={{
            height: '100%',
            background: 'white',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background: `linear-gradient(90deg, ${color}, ${alpha(color, 0.6)})`,
            },
        }}
    >
        <CardContent sx={{ p: 3 }}>
            <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                <Box flex={1}>
                    <Typography
                        variant="overline"
                        sx={{
                            color: colors.slate[500],
                            fontSize: 11,
                            fontWeight: 600,
                            letterSpacing: '0.05em',
                        }}
                    >
                        {title}
                    </Typography>
                    <Typography
                        variant="h3"
                        sx={{
                            fontWeight: 700,
                            color: colors.slate[900],
                            mt: 1,
                            mb: subtitle ? 0.5 : 0,
                        }}
                    >
                        {value.toLocaleString()}
                    </Typography>
                    {subtitle && (
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <TrendingUpIcon sx={{ fontSize: 14, color: colors.success }} />
                            <Typography
                                variant="body2"
                                sx={{ color: colors.success, fontWeight: 500, fontSize: 12 }}
                            >
                                {subtitle}
                            </Typography>
                        </Box>
                    )}
                </Box>
                <Box
                    sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '16px',
                        background: alpha(color, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: color,
                    }}
                >
                    {icon}
                </Box>
            </Box>
        </CardContent>
    </Card>
);

export const Dashboard = () => {
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const response = await fetch(`${API_URL}/api/admin/dashboard/stats`, {
                    headers: {
                        'Accept': 'application/json',
                    },
                });

                if (!response.ok) {
                    // Use mock data for now if API not available
                    setStats({
                        activeTokens: 12,
                        activeDevices: 47,
                        totalUsers: 156,
                        expiringSoon: 3,
                    });
                    return;
                }

                const data = await response.json();
                setStats(data);
            } catch {
                // Use mock data for now
                setStats({
                    activeTokens: 12,
                    activeDevices: 47,
                    totalUsers: 156,
                    expiringSoon: 3,
                });
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, []);

    if (loading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="400px"
            >
                <CircularProgress sx={{ color: colors.primary }} />
            </Box>
        );
    }

    return (
        <Box>
            {/* Page Header */}
            <Box mb={4}>
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 700,
                        color: colors.slate[900],
                        letterSpacing: '-0.5px',
                    }}
                >
                    Dashboard
                </Typography>
                <Typography
                    variant="body1"
                    sx={{ color: colors.slate[500], mt: 0.5 }}
                >
                    Welcome back! Here's an overview of your XTConnect system.
                </Typography>
            </Box>

            {/* Stats Grid */}
            <Grid container spacing={3}>
                <Grid item xs={12} sm={6} lg={3}>
                    <StatCard
                        title="Active Tokens"
                        value={stats?.activeTokens || 0}
                        icon={<KeyIcon sx={{ fontSize: 28 }} />}
                        color={colors.primary}
                        subtitle="+2 this week"
                    />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                    <StatCard
                        title="Provisioned Devices"
                        value={stats?.activeDevices || 0}
                        icon={<DevicesIcon sx={{ fontSize: 28 }} />}
                        color={colors.tertiary}
                        subtitle="+5 this month"
                    />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                    <StatCard
                        title="Total Users"
                        value={stats?.totalUsers || 0}
                        icon={<PeopleIcon sx={{ fontSize: 28 }} />}
                        color={colors.secondary}
                    />
                </Grid>
                <Grid item xs={12} sm={6} lg={3}>
                    <StatCard
                        title="Expiring Soon"
                        value={stats?.expiringSoon || 0}
                        icon={<WarningIcon sx={{ fontSize: 28 }} />}
                        color={colors.warning}
                    />
                </Grid>
            </Grid>

            {/* Quick Actions */}
            <Box mt={4}>
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 600,
                        color: colors.slate[900],
                        mb: 2,
                    }}
                >
                    Quick Actions
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6} lg={4}>
                        <Card
                            sx={{
                                p: 3,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 10px 40px -10px rgba(0,0,0,0.15)',
                                },
                            }}
                            onClick={() => window.location.hash = '#/bootstrap-tokens/create'}
                        >
                            <Box display="flex" alignItems="center" gap={2}>
                                <Box
                                    sx={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: '12px',
                                        background: `linear-gradient(135deg, ${colors.primary}, ${colors.tertiary})`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'white',
                                    }}
                                >
                                    <KeyIcon />
                                </Box>
                                <Box>
                                    <Typography
                                        sx={{
                                            fontWeight: 600,
                                            color: colors.slate[900],
                                        }}
                                    >
                                        Create Bootstrap Token
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ color: colors.slate[500] }}
                                    >
                                        Generate a new device registration token
                                    </Typography>
                                </Box>
                            </Box>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={6} lg={4}>
                        <Card
                            sx={{
                                p: 3,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 10px 40px -10px rgba(0,0,0,0.15)',
                                },
                            }}
                            onClick={() => window.location.hash = '#/devices'}
                        >
                            <Box display="flex" alignItems="center" gap={2}>
                                <Box
                                    sx={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: '12px',
                                        background: `linear-gradient(135deg, ${colors.tertiary}, ${colors.success})`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'white',
                                    }}
                                >
                                    <DevicesIcon />
                                </Box>
                                <Box>
                                    <Typography
                                        sx={{
                                            fontWeight: 600,
                                            color: colors.slate[900],
                                        }}
                                    >
                                        View Provisioned Devices
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ color: colors.slate[500] }}
                                    >
                                        Monitor connected XTNodes
                                    </Typography>
                                </Box>
                            </Box>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={6} lg={4}>
                        <Card
                            sx={{
                                p: 3,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 10px 40px -10px rgba(0,0,0,0.15)',
                                },
                            }}
                            onClick={() => window.location.hash = '#/users'}
                        >
                            <Box display="flex" alignItems="center" gap={2}>
                                <Box
                                    sx={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: '12px',
                                        background: `linear-gradient(135deg, ${colors.secondary}, #8b5cf6)`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'white',
                                    }}
                                >
                                    <PeopleIcon />
                                </Box>
                                <Box>
                                    <Typography
                                        sx={{
                                            fontWeight: 600,
                                            color: colors.slate[900],
                                        }}
                                    >
                                        Manage Users
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ color: colors.slate[500] }}
                                    >
                                        View and manage user accounts
                                    </Typography>
                                </Box>
                            </Box>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        </Box>
    );
};
